/*
 * Linsoul hero (sections/ls-hero.liquid).
 * Slides are plain DOM (photo / video / reservation card). Between slides a Three.js quad (assets/ls-three.js)
 * runs a noisy "liquid" wipe from the outgoing to the incoming photo, then hides itself; without WebGL,
 * or under reduced motion, slides crossfade. GSAP drives text entrances, the autoplay progress and the
 * reservation-card tilt. Autoplay pauses with the mouse on the words, buttons or controls (not the picture, which
 * fills most of the screen), keyboard focus, off screen, hidden tab and the pause button.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const WIDTHS = [750, 1100, 1500, 1900, 2400, 3000];

  const scripts = new Map();
  const loadScript = (src, ready) => {
    if (ready()) return Promise.resolve();
    if (!src) return Promise.reject(new Error('no src'));
    if (!scripts.has(src)) {
      scripts.set(src, new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.src = src;
        el.async = true;
        el.onload = () => (ready() ? resolve() : reject(new Error('not ready')));
        el.onerror = reject;
        document.head.appendChild(el);
      }));
    }
    return scripts.get(src);
  };
  const loadGsap = (src) => (window.lsLoadGsap
    ? window.lsLoadGsap(src)
    : loadScript(src, () => !!window.gsap).then(() => window.gsap));
  const loadThree = (src) => loadScript(src, () => !!window.LsThree).then(() => window.LsThree);
  const idle = (fn, timeout) => ('requestIdleCallback' in window
    ? window.requestIdleCallback(fn, { timeout })
    : setTimeout(fn, Math.min(timeout, 1200)));
  const within = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(() => r(null), ms))]);

  const VERT = `
    varying vec2 vUv;
    void main() {
      vUv = position.xy * .5 + .5;
      gl_Position = vec4(position.xy, 0., 1.);
    }`;
  const FRAG = `
    uniform sampler2D uA;
    uniform sampler2D uB;
    uniform vec2 uSA;
    uniform vec2 uSB;
    uniform vec2 uFA;
    uniform vec2 uFB;
    uniform vec2 uRes;
    uniform float uHA;
    uniform float uHB;
    uniform vec3 uCA;
    uniform vec3 uCB;
    uniform float uP;
    uniform float uDir;
    varying vec2 vUv;
    vec2 cover(vec2 uv, vec2 img, vec2 f) {
      float rc = uRes.x / uRes.y;
      float ri = img.x / max(img.y, 1.);
      vec2 s = rc > ri ? vec2(1., ri / rc) : vec2(rc / ri, 1.);
      return uv * s + (1. - s) * f;
    }
    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3. - 2. * f);
      return mix(mix(hash(i), hash(i + vec2(1., 0.)), u.x), mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), u.x), u.y);
    }
    float fbm(vec2 p) {
      float v = 0.;
      float a = .5;
      for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.03; a *= .5; }
      return v;
    }
    void main() {
      vec2 uv = vUv;
      float aspect = uRes.x / uRes.y;
      float n = fbm(vec2(uv.x * aspect, uv.y) * 2.2 + uP * .6);
      float x = uDir > 0. ? uv.x : 1. - uv.x;
      float e = x * .72 + n * .28;
      float m = smoothstep(e - .18, e + .18, uP * 1.36 - .18);
      float edge = m * (1. - m) * 4.;
      vec2 push = vec2(uDir * .06, 0.);
      vec2 wob = vec2(0., (n - .5) * .05 * edge);
      vec2 ua = uv - push * m + wob;
      vec2 ub = (uv - .5) * (1. - .06 * (1. - m)) + .5 + push * (1. - m) + wob;
      vec3 a = uHA > .5 ? texture2D(uA, cover(ua, uSA, uFA)).rgb : uCA;
      vec3 b = uHB > .5 ? texture2D(uB, cover(ub, uSB, uFB)).rgb : uCB;
      vec3 col = mix(a, b, m) + edge * .05;
      gl_FragColor = vec4(col, 1.);
    }`;

  const hexToRgb = (hex) => {
    const h = String(hex || '#0d0d0f').replace('#', '');
    const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0');
    return [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16) / 255);
  };

  /* The photo a slide currently shows (phone source when it applies), at a width that fits the canvas. */
  const slideImage = (slide, need) => {
    const img = slide.querySelector('.lhr-media__img');
    const video = slide.querySelector('.lhr-media__video');
    let url = '';
    let focus = [0.5, 0.5];
    if (img) {
      url = img.currentSrc;
      if (!url) {
        const source = img.closest('picture')?.querySelector('source');
        url = source && window.matchMedia(source.media).matches
          ? source.srcset.split(',')[0].trim().split(' ')[0]
          : img.src;
      }
      const pos = getComputedStyle(img).objectPosition.split(' ');
      const px = parseFloat(pos[0]);
      const py = parseFloat(pos[1]);
      focus = [Number.isFinite(px) ? px / 100 : 0.5, Number.isFinite(py) ? py / 100 : 0.5];
    } else if (video && video.poster) {
      url = video.poster;
    }
    if (!url) return null;
    const width = WIDTHS.find((w) => w >= need) || WIDTHS[WIDTHS.length - 1];
    url = /[?&]width=\d+/.test(url)
      ? url.replace(/([?&])width=\d+/, `$1width=${width}`)
      : url + (url.includes('?') ? '&' : '?') + `width=${width}`;
    return { url, focus };
  };

  function makeGL(root, THREE) {
    const canvas = document.createElement('canvas');
    canvas.className = 'lhr-gl';
    canvas.setAttribute('aria-hidden', 'true');
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
    } catch (e) {
      return null;
    }
    root.appendChild(canvas);
    const quad = new THREE.BufferGeometry();
    quad.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const blank = new THREE.Texture();
    const uniforms = {
      uA: { value: blank }, uB: { value: blank },
      uSA: { value: new THREE.Vector2(1, 1) }, uSB: { value: new THREE.Vector2(1, 1) },
      uFA: { value: new THREE.Vector2(0.5, 0.5) }, uFB: { value: new THREE.Vector2(0.5, 0.5) },
      uRes: { value: new THREE.Vector2(1, 1) },
      uHA: { value: 0 }, uHB: { value: 0 },
      uCA: { value: new THREE.Vector3(0.05, 0.05, 0.06) }, uCB: { value: new THREE.Vector3(0.05, 0.05, 0.06) },
      uP: { value: 0 }, uDir: { value: 1 },
    };
    const mesh = new THREE.Mesh(quad, new THREE.ShaderMaterial({ uniforms, vertexShader: VERT, fragmentShader: FRAG, depthTest: false, depthWrite: false }));
    mesh.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(mesh);
    const camera = new THREE.PerspectiveCamera();
    const render = () => renderer.render(scene, camera);

    let width = 1;
    const resize = () => {
      const rect = root.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.setSize(width, h, false);
      uniforms.uRes.value.set(width, h);
    };
    new ResizeObserver(resize).observe(root);
    resize();

    /* A responsive <img> uploads as an empty texture, so load a plain copy at the canvas width. */
    const cache = new Map();
    const textureOf = (slide) => {
      const pick = slideImage(slide, width * Math.min(window.devicePixelRatio || 1, 1.5));
      if (!pick) return Promise.resolve({ color: hexToRgb(slide.dataset.bg) });
      if (!cache.has(pick.url)) {
        const copy = new Image();
        copy.crossOrigin = 'anonymous';
        copy.decoding = 'async';
        copy.src = pick.url;
        cache.set(pick.url, copy.decode().then(() => {
          const tex = new THREE.Texture(copy);
          tex.needsUpdate = true;
          return { tex, w: copy.naturalWidth, h: copy.naturalHeight };
        }).catch(() => null));
      }
      return cache.get(pick.url).then((t) => (t ? { ...t, focus: pick.focus } : null));
    };
    const assign = (side, t) => {
      const has = t && t.tex;
      uniforms[`uH${side}`].value = has ? 1 : 0;
      if (has) {
        uniforms[`u${side}`].value = t.tex;
        uniforms[`uS${side}`].value.set(t.w, t.h);
        uniforms[`uF${side}`].value.set(t.focus[0], 1 - t.focus[1]);
      } else {
        const c = (t && t.color) || [0.05, 0.05, 0.06];
        uniforms[`uC${side}`].value.set(c[0], c[1], c[2]);
      }
    };

    return {
      warm: (slide) => { if (slide) textureOf(slide); },
      /* Resolves true once both photos are ready and the canvas shows the outgoing one. */
      prepare(from, to, dir, wait) {
        return within(Promise.all([textureOf(from), textureOf(to)]), wait).then((pair) => {
          if (!pair || pair.includes(null)) return false;
          assign('A', pair[0]);
          assign('B', pair[1]);
          uniforms.uDir.value = dir;
          uniforms.uP.value = 0;
          render();
          canvas.classList.add('is-on');
          return true;
        });
      },
      run(gsap, duration) {
        return gsap.to(uniforms.uP, {
          value: 1, duration, ease: 'power2.inOut', onUpdate: render,
          onComplete: () => canvas.classList.remove('is-on'),
          onInterrupt: () => canvas.classList.remove('is-on'),
        });
      },
    };
  }

  function init(root) {
    if (!root || root.dataset.lhrReady) return;
    root.dataset.lhrReady = '1';
    const slides = [...root.querySelectorAll('[data-lhr-slide]')];
    const bars = [...root.querySelectorAll('[data-lhr-go]')];
    const counter = root.querySelector('[data-lhr-current]');
    const toggle = root.querySelector('[data-lhr-toggle]');
    const track = root.querySelector('.lhr__track');
    const total = slides.length;
    const design = root.hasAttribute('data-design-mode');
    const interval = parseFloat(root.dataset.autoplay) || 0;
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    let gsap = null;
    let gl = null;
    let timer = null;
    let busy = null;
    let onScreen = true;
    let hovering = false;
    let focused = false;
    let userPaused = false;
    let editorHold = false;

    const pad = (n) => String(n).padStart(2, '0');
    const videos = (slide) => [...slide.querySelectorAll('[data-lhr-video]')];
    const syncVideos = () => {
      slides.forEach((slide, i) => videos(slide).forEach((v) => {
        if (!v.dataset.lhrBound) {
          v.dataset.lhrBound = '1';
          v.addEventListener('playing', () => v.classList.add('is-ready'));
        }
        if (i === index && onScreen && !reduce.matches && !document.hidden) {
          if (v.preload === 'none') v.preload = 'auto';
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      }));
    };
    /* Load the neighbours' photos before they're needed, so a swap never shows an empty frame. */
    const warm = () => {
      [index + 1, index - 1].forEach((i) => {
        const slide = slides[(i + total) % total];
        if (!slide || slide === slides[index]) return;
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        if (gl) gl.warm(slide);
      });
    };

    const shouldRun = () => interval > 0 && total > 1 && !design && !reduce.matches && !userPaused && !editorHold
      && !hovering && !focused && onScreen && !document.hidden;
    const updatePlay = () => {
      root.classList.toggle('is-paused', userPaused || interval === 0 || reduce.matches);
      /* held for a moment (mouse on the words, keyboard focus): the progress bar dims so the stop reads as intended */
      root.classList.toggle('is-held', !!timer && !userPaused && (hovering || focused));
      if (track) track.setAttribute('aria-live', shouldRun() ? 'off' : 'polite');
      if (timer) timer.paused(!shouldRun());
      kbTweens.forEach((tw) => tw.paused(!onScreen || document.hidden));
    };
    const startTimer = () => {
      if (timer) timer.kill();
      timer = null;
      bars.forEach((b, i) => {
        b.style.setProperty('--lhr-fill', i === index ? (interval > 0 && !reduce.matches && !userPaused ? 0 : 1) : 0);
      });
      if (!gsap || interval <= 0 || total < 2 || design || reduce.matches) return;
      const bar = bars[index];
      const state = { p: 0 };
      timer = gsap.to(state, {
        p: 1, duration: interval, ease: 'none',
        onUpdate: () => bar && bar.style.setProperty('--lhr-fill', state.p),
        onComplete: () => go(index + 1, 1),
      });
      timer.paused(!shouldRun());
    };

    /* Ken Burns: the showing slide's photo / video slowly zooms and drifts; directions alternate per slide. */
    const kbTweens = new Map();
    let kbFlip = 0;
    const kbLayers = (slide) => slide.querySelectorAll('.lhr-media__pic, .lhr-media__video');
    const kenBurns = (slide) => {
      if (!gsap || root.dataset.effect !== 'kenburns' || reduce.matches) return;
      const layers = kbLayers(slide);
      if (!layers.length) return;
      kbTweens.get(slide)?.kill();
      const zoomIn = kbFlip++ % 2 === 0;
      const dx = (Math.random() < 0.5 ? -1 : 1) * 2.5;
      const dy = (Math.random() < 0.5 ? -1 : 1) * 1.5;
      const near = { scale: 1.12, xPercent: dx, yPercent: dy };
      const far = { scale: 1.02, xPercent: 0, yPercent: 0 };
      const tw = gsap.fromTo(layers, zoomIn ? far : near, {
        ...(zoomIn ? near : far), duration: Math.max(interval, 8) + 4, ease: 'sine.inOut', overwrite: true,
      });
      tw.paused(!onScreen || document.hidden);
      kbTweens.set(slide, tw);
    };
    const kbStop = (slide) => {
      const tw = kbTweens.get(slide);
      if (!tw) return;
      tw.kill();
      kbTweens.delete(slide);
      gsap.set(kbLayers(slide), { clearProps: 'transform' });
    };

    const entrance = (slide) => {
      if (!gsap || reduce.matches) return;
      const items = slide.querySelectorAll('[data-lhr-in]');
      gsap.fromTo(items, { y: 26, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, duration: 0.8, ease: 'power3.out', stagger: 0.07, delay: 0.28, overwrite: true, clearProps: 'transform,opacity,visibility',
      });
      const card = slide.querySelector('[data-lhr-in-card]');
      if (card) {
        gsap.fromTo(card, { y: 48, rotate: -5, scale: 0.95, autoAlpha: 0 }, {
          y: 0, rotate: 0, scale: 1, autoAlpha: 1, duration: 1.1, ease: 'power3.out', delay: 0.34, overwrite: true, clearProps: 'transform,opacity,visibility',
        });
      }
    };

    const setActive = (from, to, next) => {
      from.classList.remove('is-active');
      from.classList.add('is-leaving');
      from.setAttribute('aria-hidden', 'true');
      from.inert = true;
      to.classList.add('is-active');
      to.removeAttribute('aria-hidden');
      to.inert = false;
      index = next;
      bars.forEach((b, i) => {
        b.classList.toggle('is-active', i === next);
        b.setAttribute('aria-selected', i === next ? 'true' : 'false');
      });
      if (counter) counter.textContent = pad(next + 1);
      syncVideos();
      tilt.sync();
    };

    function go(target, dir) {
      if (total < 2) return;
      const next = ((target % total) + total) % total;
      if (next === index) return;
      if (busy) busy.finish();
      const from = slides[index];
      const to = slides[next];
      const direction = dir || (next > index ? 1 : -1);
      const leaving = [];
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        leaving.forEach((t) => t.progress(1).kill());
        from.classList.remove('is-leaving');
        if (gsap) kbStop(from);
        const fromBody = from.querySelector('.lhr-body');
        if (fromBody) fromBody.style.removeProperty('opacity');
        const media = to.querySelector('.lhr-media');
        if (media) media.style.removeProperty('opacity');
        busy = null;
        warm();
        startTimer();
      };
      busy = { finish };
      if (timer) timer.pause();

      if (!gsap) {
        setActive(from, to, next);
        finish();
        return;
      }
      const fromBody = from.querySelector('.lhr-body');
      if (fromBody && !reduce.matches) leaving.push(gsap.to(fromBody, { opacity: 0, duration: 0.3, ease: 'power1.out' }));

      const useGL = gl && root.dataset.effect === 'shader' && !reduce.matches;
      (useGL ? gl.prepare(from, to, direction, 400) : Promise.resolve(false)).then((ready) => {
        if (done) return;
        setActive(from, to, next);
        entrance(to);
        kenBurns(to);
        if (ready) {
          leaving.push(gl.run(gsap, 1.15));
          leaving[leaving.length - 1].eventCallback('onComplete', () => {
            root.querySelector('.lhr-gl')?.classList.remove('is-on');
            finish();
          });
        } else {
          const media = to.querySelector('.lhr-media');
          const fade = reduce.matches ? 0.2 : root.dataset.effect === 'kenburns' ? 1.2 : 0.8;
          const t = gsap.fromTo(media, { opacity: 0 }, { opacity: 1, duration: fade, ease: 'power2.out', onComplete: finish });
          leaving.push(t);
        }
      });
    }

    /* Reservation card: pointer tilt + a slow sheen sweep, only while its slide is showing. */
    const tilt = (() => {
      const cards = [...root.querySelectorAll('[data-lhr-tilt]')];
      const loops = new Map();
      const setups = new Map();
      const setup = (card) => {
        if (setups.has(card) || !gsap || reduce.matches) return setups.get(card);
        const stage = card.parentElement;
        const k = Number(card.dataset.lhrTilt) || 1;
        const lift = Number(card.dataset.lhrLift) || 1;
        const angle = { x: 0, y: 0 };
        const apply = () => {
          card.style.setProperty('--rx', `${angle.x.toFixed(2)}deg`);
          card.style.setProperty('--ry', `${angle.y.toFixed(2)}deg`);
        };
        const rx = gsap.quickTo(angle, 'x', { duration: 0.6, ease: 'power3.out', onUpdate: apply });
        const ry = gsap.quickTo(angle, 'y', { duration: 0.6, ease: 'power3.out', onUpdate: apply });
        if (fine.matches) {
          stage.addEventListener('pointermove', (e) => {
            const r = stage.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width - 0.5;
            const y = (e.clientY - r.top) / r.height - 0.5;
            rx(-y * 10 * k);
            ry(x * 12 * k);
            gsap.to(card, { '--s': lift, '--mx': `${Math.round((x + 0.5) * 100)}%`, '--my': `${Math.round((y + 0.5) * 100)}%`, '--sheen': 0.7, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
          });
          stage.addEventListener('pointerleave', () => {
            rx(0);
            ry(0);
            gsap.to(card, { '--s': 1, '--sheen': 0.35, duration: 0.6, ease: 'power2.out', overwrite: 'auto' });
          });
        }
        setups.set(card, true);
        return true;
      };
      return {
        sync() {
          if (!gsap || reduce.matches) return;
          cards.forEach((card) => {
            const active = card.closest('[data-lhr-slide]') === slides[index] && onScreen;
            setup(card);
            if (active && !loops.has(card)) {
              const tl = gsap.timeline({ repeat: -1, defaults: { ease: 'sine.inOut' } });
              tl.to(card, { '--ty': '-10px', duration: 3.2 }).to(card, { '--ty': '0px', duration: 3.2 });
              const sweep = gsap.fromTo(card, { '--mx': '-30%' }, { '--mx': '130%', duration: 2.4, ease: 'power2.inOut', repeat: -1, repeatDelay: 4.5, delay: 1.2 });
              loops.set(card, [tl, sweep]);
            } else if (!active && loops.has(card)) {
              loops.get(card).forEach((t) => t.kill());
              loops.delete(card);
            }
          });
        },
      };
    })();

    /* Controls */
    root.querySelector('[data-lhr-prev]')?.addEventListener('click', () => go(index - 1, -1));
    root.querySelector('[data-lhr-next]')?.addEventListener('click', () => go(index + 1, 1));
    bars.forEach((b) => b.addEventListener('click', () => go(parseInt(b.dataset.lhrGo, 10))));
    toggle?.addEventListener('click', () => {
      userPaused = !userPaused;
      toggle.setAttribute('aria-label', userPaused ? root.dataset.playLabel : root.dataset.pauseLabel);
      if (!userPaused && timer && timer.progress() >= 1) go(index + 1, 1);
      else if (!timer) startTimer();
      updatePlay();
    });
    root.addEventListener('keydown', (e) => {
      if (e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1, -1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1, 1); }
    });
    root.addEventListener('focusin', (e) => { focused = e.target.matches(':focus-visible'); updatePlay(); });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focused = false; updatePlay(); } });
    if (fine.matches) {
      const HOLD = '.lhr-copy, .lhr-nav__group, .lhr-body--cards a';
      root.addEventListener('pointerover', (e) => {
        if (e.pointerType !== 'mouse') return;
        const on = !!e.target.closest(HOLD);
        if (on !== hovering) { hovering = on; updatePlay(); }
      });
      root.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse' && hovering) { hovering = false; updatePlay(); } });
    }

    /* Swipe (touch / pen). A swipe never also follows a sale slide's link. */
    let sx = 0;
    let sy = 0;
    let swiped = false;
    root.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      sx = e.clientX;
      sy = e.clientY;
      swiped = false;
    }, { passive: true });
    root.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'mouse' || total < 2) return;
      const dx = e.clientX - sx;
      const dy = e.clientY - sy;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        swiped = true;
        go(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
      }
    }, { passive: true });
    root.addEventListener('click', (e) => {
      if (swiped) { e.preventDefault(); e.stopPropagation(); swiped = false; }
    }, true);

    new IntersectionObserver((entries) => {
      onScreen = entries[0].isIntersecting;
      syncVideos();
      tilt.sync();
      updatePlay();
    }, { threshold: 0.2 }).observe(root);
    document.addEventListener('visibilitychange', () => { syncVideos(); updatePlay(); });
    reduce.addEventListener?.('change', () => { syncVideos(); startTimer(); updatePlay(); });

    /* Theme editor: selecting a slide block shows it and holds autoplay. */
    if (design) {
      document.addEventListener('shopify:block:select', (e) => {
        const i = slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i < 0) return;
        editorHold = true;
        go(i);
        updatePlay();
      });
      document.addEventListener('shopify:block:deselect', () => { editorHold = false; updatePlay(); });
    }

    slides.forEach((s, i) => { if (i !== index) s.inert = true; });
    syncVideos();
    updatePlay();
    loadGsap(root.dataset.gsapSrc).then((g) => {
      gsap = g;
      kenBurns(slides[index]);
      startTimer();
      tilt.sync();
      updatePlay();
    }).catch(() => {});

    if (total > 1 && root.dataset.effect === 'shader' && !reduce.matches) {
      const boot = () => idle(() => {
        loadThree(root.dataset.threeSrc).then((THREE) => {
          gl = makeGL(root, THREE);
          warm();
        }).catch(() => {});
      }, 2500);
      if (document.readyState === 'complete') boot();
      else window.addEventListener('load', boot, { once: true });
    }
    warm();
  }

  /* Sale Reservation Cards slide countdown: the code is inline in ls-hero.liquid (window.lsHeroCount) so the first paint is right;
     this catches sections the theme editor reloads, whose inline scripts don't run. countOne is the same code, for a page
     cached from before the inline copy existed. */
  const countOne = (el) => {
    if (el.hasAttribute('data-lhr-ready')) return;
    el.dataset.lhrReady = '';
    const z = /^([+-])(\d{1,2}):?(\d{2})?/.exec(el.dataset.tz || '+08:00');
    const off = z ? (z[1] === '-' ? -1 : 1) * (+z[2] * 60 + +(z[3] || 0)) * 60000 : 0;
    const at = (v) => { const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(String(v || '').trim()); return m ? Date.UTC(+m[1], m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) - off : null; };
    const from = at(el.dataset.from);
    const to = at(el.dataset.to);
    const label = el.querySelector('[data-lhr-count-label]');
    const out = el.querySelector('[data-lhr-count-time]');
    const p2 = (n) => String(n).padStart(2, '0');
    let id = 0;
    const tick = () => {
      const now = Date.now();
      const until = from && now < from ? from : to && now < to ? to : null;
      label.textContent = until === from && from ? el.dataset.soon : until ? el.dataset.live : el.dataset.ended;
      if (!until) { out.textContent = ''; clearInterval(id); return; }
      const t = Math.floor((until - now) / 1000);
      const d = Math.floor(t / 86400);
      out.textContent = (d ? d + 'd ' : '') + p2(Math.floor(t / 3600) % 24) + ':' + p2(Math.floor(t / 60) % 60) + ':' + p2(t % 60);
    };
    tick();
    id = setInterval(tick, 1000);
  };
  const counts = (scope) => scope.querySelectorAll('[data-lhr-count]:not([data-lhr-ready])').forEach((el) => (window.lsHeroCount || countOne)(el));

  const start = () => { document.querySelectorAll('[data-lhr]').forEach(init); counts(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => { e.target.querySelectorAll('[data-lhr]').forEach(init); counts(e.target); });
})();
